/**
 * @file SiteSettingsContext.tsx
 * @description React Context for site-wide settings (favicons, SEO defaults, integrations, etc.)
 * fetched once per locale from Contentstack and made available to the component tree.
 */

'use client';

import { createContext, useContext } from 'react';

import { ISiteSettings } from '@/.generated';

// Context value interface for site settings.
interface SiteSettingsContextValue {
  // Site settings entry from CMS (partial — keys may be missing in non-default locales).
  siteSettings: Partial<ISiteSettings>;
}

// React Context for site settings.
const SiteSettingsContext = createContext<SiteSettingsContextValue>({
  siteSettings: {},
});

// Provider for SiteSettingsContext. Wrap consumers that need site-settings access.
export const SiteSettingsProvider = SiteSettingsContext.Provider;

// Custom hook to access site settings from context. Must be used within a SiteSettingsProvider.
export const useSiteSettings = () => useContext(SiteSettingsContext);
